import { Icon, Tooltip } from "@blueprintjs/core";
import { Popover2 } from "@blueprintjs/popover2";
import * as React from "react";

import {
  DogReferentialItem,
  ISearchDogReferentialQuery,
  PaginatedSearch,
  ReferentialApi,
  ReferentialItemDto,
  SearchDogReferentialQuery,
} from "../../api";
import { useApiService, useTl } from "../../hooks";
import { ETLCodes } from "../../locales";
import { FGAdvancedSearchInput, IFGAdvancedSearchInputProps } from "./FGAdvancedSearchInput";
import { ToolTipContainer } from "./FGPeopleSearchInput";

export interface IFGDogSearchInputProps
  extends Omit<IFGAdvancedSearchInputProps<ReferentialItemDto>, "searchFn" | "getDisplayedDataFn"> {
  defaultSearch?: ISearchDogReferentialQuery;
}

export const FGDogSearchInput: React.FunctionComponent<IFGDogSearchInputProps> = ({ defaultSearch, ...fgProps }) => {
  const refApi = useApiService(ReferentialApi);
  const { t } = useTl();
  const searchFn = React.useCallback(
    async (sObj: PaginatedSearch) => {
      const query = SearchDogReferentialQuery.fromJS({
        ...defaultSearch,
        sObj,
      });
      return refApi.searchDogs(query);
    },
    [defaultSearch, refApi]
  );

  const [displayedData, setDisplayedData] = React.useState<DogReferentialItem>();

  const getDisplayedDataFn = React.useCallback(
    async (id: any) => {
      const d = await refApi.searchDogs(
        new SearchDogReferentialQuery({
          dogId: id,
        })
      );
      setDisplayedData(d.results.length > 0 ? d.results[0] : null);
      return d.results.length > 0 ? d.results[0] : null;
    },
    [refApi]
  );

  return (
    <>
      <FGAdvancedSearchInput
        {...fgProps}
        searchFn={searchFn}
        getDisplayedDataFn={getDisplayedDataFn}
        showTableHeader={true}
        rightElement={
          !!displayedData?.remark ? (
            <Popover2 content={<ToolTipContainer>{displayedData?.remark}</ToolTipContainer>} interactionKind={"hover"}>
              <div style={{ marginLeft: "0.5rem", marginRight: "0.5rem" }}>
                <Icon icon={"warning-sign"} intent={"danger"} />
              </div>
            </Popover2>
          ) : null
        }
        columns={[
          {
            fieldName: "displayValue",
            // computed: true,
            disableSorting: true,
            header: () => t(ETLCodes.Name),
          },
          {
            fieldName: "pedigrees",
            computed: true,
            disableSorting: true,
            header: () => t(ETLCodes.Pedigree),
            render: (row: DogReferentialItem) => {
              if (row.pedigrees?.length > 0) {
                return (
                  <span style={{ textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {row.pedigree}
                    {row.pedigrees.length > 1 && (
                      <span>
                        &nbsp;
                        <Tooltip
                          content={
                            <div>
                              {row.pedigrees.map((pedigree, i) => (
                                <div key={i}>{pedigree}</div>
                              ))}
                            </div>
                          }
                          position="right"
                        >
                          <div style={{ cursor: "pointer" }}>...</div>
                        </Tooltip>
                      </span>
                    )}
                  </span>
                );
              } else {
                return <div />;
              }
            },
          },
          {
            header: () => t(ETLCodes.Identification1),
            computed: true,
            disableSorting: true,
            fieldName: "dogChip",
          },
          {
            header: () => t(ETLCodes.Identification2),
            computed: true,
            disableSorting: true,
            fieldName: "secondDogChip",
          },
        ]}
      />
    </>
  );
};
